/**
 * Page class is intended to capture different properties for HTML pages
 * Supply those properties for services to use them (ie. analytics to mParticle).
 *
 * Any specific formatting of properties or types should be done in concrete classes for the third-part provider, not
 * here.
 */
import { AdService } from '../services/ads'
import { AnalyticsService } from '../services/analytics'
import { IdentityEvent, IdentityEvents, IIdentityEvent } from '../services/common/events/identity'
import { Profile } from '../services/common/profile'
import { IdentityService } from '../services/identity'
import { ISubscription } from '../services/identity/adapters/adapter'
import { IConfiguration } from './conf'

export class Page {
    public profile: Profile

    constructor(
        private conf: IConfiguration,
        private adService: AdService,
        private analyticsService: AnalyticsService,
        private identityService: IdentityService) {

        // init identity service first; dependent services init'd in change handler
        if (this.conf.identity) {
            this.identityService
                .addEventListener(IdentityEvents.IDENTITY_INIT, this.identityInit.bind(this))
                .addEventListener(IdentityEvents.IDENTITY_CHANGE, this.identityChange.bind(this))
                .initialize(conf.identity.sso, conf.identity.subscriber)
        } else {
            this.identityInit(IdentityEvent(IdentityEvents.IDENTITY_INIT, new Profile()))
        }
    }

    private identityChange(event: IIdentityEvent) {
        this.profile = event.detail

        // update subscription-related data (e.g. 'ad light' flag)
        this.updateSubscriberDetails()
        this.identityService.update(this.profile, false).catch(console.log)

        // update ad service
        this.adService.update(this.profile.adLight)

        // update analytics user
        this.analyticsService.onIdentityChange(this.profile)
    }

    /**
     * Actions requiring user profile.
     */
    private identityInit(event: IIdentityEvent) {
        this.profile = event.detail

        // update subscription-related data (e.g. 'ad light' flag)
        this.updateSubscriberDetails()
        this.identityService.update(this.profile, false).catch(console.log)

        // initialize ad service
        if (this.conf.ads && this.adService) {
            this.adService.initialize({
                adLight: this.profile.adLight,
                ancestry: this.conf.page.ancestry,
                articleId: this.conf.page.articleId,
                authors: this.conf.page.authors,
                bidding: this.conf.ads.bidding,
                network: this.conf.ads.network,
                originId: this.conf.page.originId,
                page: this.conf.page.type,
                property: this.conf.ads.property,
                provider: this.conf.ads.provider,
                siteId: this.conf.ads.site,
                verifier: this.conf.ads.verifier
            })
        }

        // initialize analytics
        if (this.conf.analytics && this.analyticsService) {
            this.analyticsService.initialize({
                adSite: this.conf.ads.site,
                brand: this.conf.analytics.brand,
                debug: this.conf.analytics.debug,
                division: this.conf.analytics.division,
                domain: this.conf.analytics.domain,
                page: this.conf.page,
                profile: this.profile,
                provider: this.conf.analytics.provider,
                vendor: this.conf.analytics.vendor
            })
                .catch(console.log)
        }
    }

    private updateSubscriberDetails() {
        this.profile.adLight = (this.profile.subscriptions || []).reduce((pass, sub: ISubscription) => {
            return pass || (this.conf.identity.subscriber.resIds || []).indexOf(sub.id) >= 0
        }, false)
    }
}
